import request from '../../../../api';
import { Teacher } from '../../../../interfaces/examiner/Teacher';
import { Exam } from '../../../../interfaces/examiner/Exam';
import { RecipientRow, SchoolGroup, Selection, ListSelectionFilter } from './types';

export const PAGE_SIZE = 500; // max allowed by backend
export const ROW_HEIGHT = 48;

export function estimateListContentWidth(labels: string[], containerWidth: number): number {
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return Math.max(containerWidth, longest);
}

export const PRIMARY_TYPO = {
  style: { fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' },
} as const;
export const SECONDARY_TYPO = { style: { fontSize: 11, whiteSpace: 'nowrap' } } as const;

export function isHtmlBodyEmpty(html: string): boolean {
  return !html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
}

export async function fetchAllPages<T>(
  buildUrl: (offset: number) => string,
  getItems: (data: any) => T[],
  getTotal: (data: any) => number,
  onProgress: (loaded: number, total: number) => void
): Promise<T[]> {
  let all: T[] = [];
  let offset = 0;
  let total = Infinity;

  while (offset < total) {
    const res = await request(buildUrl(offset));
    const data = await res.json();
    const items = getItems(data);
    total = getTotal(data);
    all = all.concat(items);
    offset += items.length;
    onProgress(all.length, total);
    if (items.length === 0) break; // safety guard
  }

  return all;
}

export function getActiveTeacherIds(teachers: Teacher[]): Set<string> {
  return new Set(teachers.map((teacher) => teacher.id));
}

export function getExamTeacherIds(exam: Exam, activeTeacherIds: Set<string>): string[] {
  const ids: string[] = [];
  exam.teachers?.forEach((eht) => {
    const id = eht.teacherId ?? eht.teacher?.id;
    if (id && activeTeacherIds.has(id)) ids.push(id);
  });
  return ids;
}

export function examHasNoActiveTeachers(exam: Exam, activeTeacherIds: Set<string>): boolean {
  const linkedTeachers = exam.teachers ?? [];
  if (!linkedTeachers.length) return false;
  return getExamTeacherIds(exam, activeTeacherIds).length === 0;
}

export function isExamFullySelected(
  exam: Exam,
  selections: Selection[],
  activeTeacherIds: Set<string>
): boolean {
  const teacherIds = getExamTeacherIds(exam, activeTeacherIds);
  if (!teacherIds.length) return false;
  const selectedTeacherIds = new Set(
    selections.filter((s) => s.type === 'teacher').map((s) => s.id)
  );
  return teacherIds.every((id) => selectedTeacherIds.has(id));
}

export function addExamToSelections(
  selections: Selection[],
  exam: Exam,
  teacherById: Map<string, Teacher>,
  activeTeacherIds: Set<string>
): Selection[] {
  const examId = exam.id;
  const next = [...selections];

  getExamTeacherIds(exam, activeTeacherIds).forEach((teacherId) => {
    const teacher = teacherById.get(teacherId);
    if (!teacher) return;

    const idx = next.findIndex((s) => s.type === 'teacher' && s.id === teacherId);
    if (idx >= 0) {
      const existing = next[idx];
      const examIds = [...(existing.examIds ?? [])];
      if (!examIds.includes(examId)) examIds.push(examId);
      next[idx] = { ...existing, examIds };
      return;
    }

    next.push({
      type: 'teacher',
      id: teacherId,
      label: teacher.name,
      examIds: [examId],
    });
  });

  return next;
}

export function removeAllExamTeachersFromSelections(
  selections: Selection[],
  exam: Exam,
  activeTeacherIds: Set<string>
): Selection[] {
  const examTeacherIds = new Set(getExamTeacherIds(exam, activeTeacherIds));
  return selections.filter((sel) => sel.type !== 'teacher' || !examTeacherIds.has(sel.id));
}

export function getSelectedExamIds(
  exams: Exam[],
  selections: Selection[],
  activeTeacherIds: Set<string>
): Set<string> {
  return new Set(
    exams
      .filter((exam) => isExamFullySelected(exam, selections, activeTeacherIds))
      .map((exam) => exam.id)
  );
}

export function matchesSelectionFilter(isSelected: boolean, filter: ListSelectionFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'selected') return isSelected;
  return !isSelected;
}

export function getTeacherLocations(teachers: Teacher[]): Teacher['location'][] {
  const locations = new Set<Teacher['location']>();
  teachers.forEach((teacher) => {
    if (teacher.location) locations.add(teacher.location);
  });
  return Array.from(locations).sort((a, b) => a.localeCompare(b));
}

export function invertTeacherSelectionsForTeachers(
  selections: Selection[],
  teachers: Teacher[]
): Selection[] {
  const visibleIds = new Set(teachers.map((teacher) => teacher.id));
  const selectedVisibleIds = new Set(
    selections
      .filter((selection) => selection.type === 'teacher' && visibleIds.has(selection.id))
      .map((selection) => selection.id)
  );
  const next = selections.filter(
    (selection) => !(selection.type === 'teacher' && visibleIds.has(selection.id))
  );
  teachers.forEach((teacher) => {
    if (!selectedVisibleIds.has(teacher.id)) {
      next.push({
        type: 'teacher',
        id: teacher.id,
        label: teacher.name,
        manual: true,
      });
    }
  });
  return next;
}

export function getSchoolTeacherIds(school: SchoolGroup): string[] {
  return school.teachers.map((teacher) => teacher.id);
}

export function isSchoolFullySelected(school: SchoolGroup, selections: Selection[]): boolean {
  const teacherIds = getSchoolTeacherIds(school);
  if (!teacherIds.length) return false;
  const selectedTeacherIds = new Set(
    selections.filter((s) => s.type === 'teacher').map((s) => s.id)
  );
  return teacherIds.every((id) => selectedTeacherIds.has(id));
}

export function addSchoolToSelections(
  selections: Selection[],
  school: SchoolGroup,
  teacherById: Map<string, Teacher>
): Selection[] {
  const schoolId = school.name;
  const next = [...selections];

  getSchoolTeacherIds(school).forEach((teacherId) => {
    const teacher = teacherById.get(teacherId);
    if (!teacher) return;

    const idx = next.findIndex((s) => s.type === 'teacher' && s.id === teacherId);
    if (idx >= 0) {
      const existing = next[idx];
      const schoolIds = [...(existing.schoolIds ?? [])];
      if (!schoolIds.includes(schoolId)) schoolIds.push(schoolId);
      next[idx] = { ...existing, schoolIds };
      return;
    }

    next.push({
      type: 'teacher',
      id: teacherId,
      label: teacher.name,
      schoolIds: [schoolId],
    });
  });

  return next;
}

export function removeAllSchoolTeachersFromSelections(
  selections: Selection[],
  school: SchoolGroup
): Selection[] {
  const schoolTeacherIds = new Set(getSchoolTeacherIds(school));
  return selections.filter((sel) => sel.type !== 'teacher' || !schoolTeacherIds.has(sel.id));
}

export function getSelectedSchoolIds(
  schoolGroups: SchoolGroup[],
  selections: Selection[]
): Set<string> {
  return new Set(
    schoolGroups
      .filter((school) => isSchoolFullySelected(school, selections))
      .map((school) => school.name)
  );
}

export function getSchoolGroups(teachers: Teacher[]): SchoolGroup[] {
  const map: Record<string, Teacher[]> = {};
  teachers.forEach((t) => {
    const school = t.school || 'Unknown';
    if (!map[school]) map[school] = [];
    map[school].push(t);
  });
  return Object.entries(map)
    .map(([name, grouped]) => ({ name, teacherCount: grouped.length, teachers: grouped }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function buildRecipientRows(
  selections: Selection[],
  exams: Exam[],
  schoolGroups: SchoolGroup[],
  teacherById: Map<string, Teacher>
): RecipientRow[] {
  const rows = new Map<string, RecipientRow>();

  const addTeacher = (teacherId: string | undefined, examTitle: string | null) => {
    if (!teacherId) return;
    const teacher = teacherById.get(teacherId);
    if (!teacher) return;

    const existing = rows.get(teacherId);
    if (existing) {
      if (examTitle && !existing.examTitles.includes(examTitle)) {
        existing.examTitles.push(examTitle);
      }
      return;
    }

    rows.set(teacherId, {
      teacherId,
      teacherName: teacher.name?.trim() || 'teacher',
      school: teacher.school?.trim() || '-',
      examTitles: examTitle ? [examTitle] : [],
      email: teacher.email?.trim() || '-',
    });
  };

  selections.forEach((sel) => {
    if (sel.type === 'teacher') {
      if (sel.examIds?.length) {
        sel.examIds.forEach((examId) => {
          const exam = exams.find((e) => e.id === examId);
          addTeacher(sel.id, exam?.title?.trim() || examId);
        });
      } else {
        addTeacher(sel.id, null);
      }
    } else if (sel.type === 'school') {
      const group = schoolGroups.find((g) => g.name === sel.id);
      group?.teachers.forEach((t) => addTeacher(t.id, null));
    } else if (sel.type === 'exam') {
      const exam = exams.find((e) => e.id === sel.id);
      const examTitle = exam?.title?.trim() || sel.label;
      exam?.teachers?.forEach((eht) => addTeacher(eht.teacherId ?? eht.teacher?.id, examTitle));
    }
  });

  return Array.from(rows.values()).sort(
    (a, b) => a.teacherName.localeCompare(b.teacherName) || a.email.localeCompare(b.email)
  );
}

export function computeTotalRecipients(
  selections: Selection[],
  exams: Exam[],
  schoolGroups: SchoolGroup[],
  teacherById: Map<string, Teacher>
): number {
  return buildRecipientRows(selections, exams, schoolGroups, teacherById).length;
}
